import { useEffect } from 'react';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import cx from 'classnames';
import type { EventFormData } from '../RestaurantEventForm';
import styles from '../RestaurantEventForm.scss';
import stepStyles from './GuestCapacityStep.scss';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import { useIsOpenWithData } from '@shared/hooks/useIsOpenWithData';
import { AssignTablesModal } from './AssignTablesModal';
import { TicketTierRow } from './TicketTierRow';
import { TotalCapacityField } from './TotalCapacityField';

const BOOKING_UNIT_OPTIONS = [
  { label: 'Seat', value: 'seat' },
  { label: 'Table', value: 'table' },
];

export const GuestCapacityStep = ({
  control,
  watch,
  setValue,
}: {
  control: Control<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
}) => {
  const {
    isOpen,
    close,
    open,
    data: activeTierIndex,
  } = useIsOpenWithData<number>();

  const handleOpenAssignTables = (index: number) => open(index);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ticketTiers',
  });

  const ticketTiers = watch('ticketTiers');
  const tierCapacitySum = (ticketTiers ?? []).reduce(
    (sum, tier) => sum + (Number(tier.capacity) || 0),
    0,
  );

  useEffect(() => {
    setValue('capacity', String(tierCapacitySum));
  }, [tierCapacitySum, setValue]);

  return (
    <section
      className={cx(
        styles.eventFormStep,
        stepStyles.eventFormStepGuestCapacity,
      )}
    >
      <p className={styles.stepSubtitle}>Configure guest capacity</p>
      <ControlledFormSelect<EventFormData>
        control={control}
        name="bookingUnit"
        label="Booking unit"
        options={BOOKING_UNIT_OPTIONS}
        labelClassName={styles.fieldLabel}
        className={stepStyles.bookingUnitField}
        darkMode
        tooltipText="Whether guests see this event sold by seat or by table. Controls the wording in the booking flow."
      />
      <TotalCapacityField control={control} name="capacity" />

      <div className={styles.formRowWrap}>
        <header>
          <div className={styles.fieldLabel}>Ticket Tiers</div>
          <Button
            label="Add Tier"
            variant={ButtonVariants.Secondary}
            onClick={() =>
              append({
                name: '',
                description: '',
                price: '',
                capacity: '',
                tableCount: '',
                floorPlanTableIds: [],
              })
            }
            iconName="plus"
          />
        </header>

        <SimpleBar className={stepStyles.ticketTiersList}>
          {fields.map((field, index) => (
            <TicketTierRow
              key={field.id}
              index={index}
              control={control}
              onOpenAssignTables={handleOpenAssignTables}
              onRemove={remove}
            />
          ))}
        </SimpleBar>

        {activeTierIndex != null && (
          <AssignTablesModal
            isOpen={isOpen}
            tierIndex={activeTierIndex}
            onClose={close}
            onDone={close}
            watch={watch}
            setValue={setValue}
          />
        )}
      </div>
    </section>
  );
};
